import { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Stack,
  IconButton,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const isValidEmail = (email) => {
  return /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/.test(email);
};

const getInitialFormData = (fields) => {
  return fields.reduce((acc, field) => {
    acc[field.name] = field.defaultValue || (field.type === "file" ? null : "");
    return acc;
  }, {});
};




// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useFieldErrors = () => {
  const [errors, setErrors] = useState({});

  const validateField = useCallback((name, value, type) => {
    if (type === "email") {
      if (value && !isValidEmail(value)) {
        setErrors((prev) => ({ ...prev, [name]: "Email Inválido" }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  }, []);

  return { errors, validateField };
};

const useFormValidation = (fields, formData, errors) => {
  return useMemo(() => {
    return fields.every((field) => {
      if (!field.required) return true;

      const value = formData[field.name];
      const hasError = errors[field.name];

      if (hasError) return false;
      if (field.type === "file") return value !== null;

      return value !== "" && value !== undefined && value !== null;
    });
  }, [fields, formData, errors]);
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const FieldRenderer = ({ field, value, onChange, error }) => {
  const commonTextFieldSx = {
    "& label.Mui-focused": { color: "primary.main" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#ccc" },
      "&:hover fieldset": { borderColor: "#999" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
  };

  switch (field.type) {
    case "select":
      return (
        <FormControl fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value || ""}
            label={field.label}
            onChange={(e) => onChange(field.name, e.target.value)}
          >
            {field.options?.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case "file":
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {field.label}
          </Typography>

          {!value ? (
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ backgroundColor: "primary.main", color: "white" }}
              aria-label={`Escolher ficheiro para ${field.label}`}
            >
              Escolher ficheiro
              <input
                type="file"
                hidden
                onChange={(e) => onChange(field.name, e.target.files[0] || null)}
              />
            </Button>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  bgcolor: "#f5f5f5",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  flex: 1,
                }}
              >
                {value?.name}
              </Typography>
              <IconButton
                color="error"
                onClick={() => onChange(field.name, null)}
                aria-label="Remover ficheiro"
              >
                <Delete />
              </IconButton>
            </Stack>
          )}
        </Box>
      );

    default:
      return (
        <TextField
          fullWidth
          type={field.type}
          label={field.label}
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          helperText={error || ""}
          error={Boolean(error)}
          inputProps={{
            autoComplete: "new-password",
          }}
          sx={commonTextFieldSx}
        />
      );
  }
};

FieldRenderer.displayName = "FieldRenderer";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function EditorView({ title, fields = [], onSubmit, buttonTitle, oncloseBudgetEditor }) {
  const [formData, setFormData] = useState(() => getInitialFormData(fields));
  const { errors, validateField } = useFieldErrors();
  const [submitting, setSubmitting] = useState(false);

  const isFormValid = useFormValidation(fields, formData, errors);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    const field = fields.find((f) => f.name === name);
    if (field?.type === "email") {
      validateField(name, value, "email");
    }
  };


  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!onSubmit || !isFormValid || submitting) return;

      try {
        setSubmitting(true);
        await onSubmit(formData);
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit, formData, isFormValid, submitting]
  );

  const closeForm = () => {
    if (oncloseBudgetEditor) {
      oncloseBudgetEditor();
    }
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
      <Card sx={{ width: 600, p: 2, borderRadius: 3, boxShadow: 4 }}>
        <CardHeader
          title={title || "Formulário Dinâmico"}
          sx={{ textAlign: "center", position: "relative" }}
          action={
            <IconButton
              aria-label="fechar"
              onClick={() => closeForm()}
              sx={{
                backgroundColor: "error.main",
                color: "#fff",
                position: "absolute",
                right: 10,
                top: 10,
              }}
            >
              <CloseIcon />
            </IconButton>
          }
        />

        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {fields.map((field) => (
                <FieldRenderer
                  field={field}
                  value={formData[field.name]}
                  onChange={handleChange}
                  error={errors[field.name]}
                />
              ))}

              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{ backgroundColor: "primary.main", color: "white" }}
                disabled={!isFormValid || submitting}
              >
                {submitting ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  buttonTitle
                )}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
EditorView.propTypes = {
  title: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string),
      defaultValue: PropTypes.any,
    })
  ),
  onSubmit: PropTypes.func,
  buttonTitle: PropTypes.string,
  oncloseBudgetEditor: PropTypes.func,
};

EditorView.defaultProps = {
  fields: [],
};

export default EditorView;
